import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';
import VolDeMouettesCommunautes from '@/components/presentational/img/v2024/VolDeMouettesCommunautes.svg';
import { Group } from '@/types/core/Group';
import { SearchResult } from '@/types/search/searchresult';
import {
  Box,
  Button,
  Flex,
  Icon,
  Show,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import PublishGroupModal from '@components/community/group/PublishGroupModal';
import CardBody from '@components/presentational/Cards/Parts/CardBody';
import SimpleCard from '@components/presentational/Cards/SimpleCard';
import Chapo from '@components/presentational/Chapo';
import MobileBottomNav from '@components/presentational/MobileBottomNav';
import UIWidth from '@components/presentational/UIWidth';
import GroupsHomeNavbar from '@components/ressource/groups/GroupsHomeNavbar';
import React, { useEffect, useMemo } from 'react';
import { PiNotePencilThin, PiUsersThin } from 'react-icons/pi';
import ModalHeader from '@components/presentational/CustomModal/ModalHeader';
import ModalBody from '@components/presentational/CustomModal/ModalBody';
import ModalFooter from '@components/presentational/CustomModal/ModalFooter';
import Modal from '@components/presentational/CustomModal/Modal';
import { useGroup } from '@components/app/hooks/useGroup';
import PublishDiscussionModal from '@components/community/discussion/PublishDiscussionModal';
import { useRouter } from 'next/router';
import { useGoogleTagManager } from '@components/app/hooks/useGTM';
import { getGroup } from '@/api/groups';

interface LayoutGroupCommunityProps {
  children: React.ReactNode;
  userGroups: SearchResult<Group>;
  thematicsGroups: SearchResult<Group>;
  countryGroups: SearchResult<Group>;
  selectedGroup?: Group | null;
  onEndPublicationCreated?: () => void;
}

export default function LayoutGroupCommunity({
  userGroups,
  thematicsGroups,
  countryGroups,
  selectedGroup,
  children,
  onEndPublicationCreated,
}: LayoutGroupCommunityProps) {
  const toast = useToast();
  const { replace, route, query } = useRouter();
  const { sendDataToGTM } = useGoogleTagManager();

  const {
    isOpen: isGroupModalOpen,
    onOpen: onGroupModalOpen,
    onClose: onGroupModalClose,
  } = useDisclosure();

  const {
    isOpen: isMobileGroupModalOpen,
    onOpen: onMobileGroupModalOpen,
    onClose: onMobileGroupModalClose,
  } = useDisclosure();

  const {
    isOpen: isMobileCreateDiscussionOpen,
    onOpen: onMobileCreateDiscussionOpen,
    onClose: onMobileCreateDiscussionClose,
  } = useDisclosure();
  const { isMember } = useGroup(selectedGroup);

  // We filtered userGroups from thematics and country groups.
  const filteredThematicsGroups = useMemo<Group[]>(() => {
    if (!userGroups || !thematicsGroups) return [];
    return thematicsGroups.items.filter(
      group => !userGroups.items.find(userGroup => userGroup.id === group.id)
    );
  }, [userGroups, thematicsGroups]);

  const filtetedCountryGroups = useMemo<Group[]>(() => {
    if (!userGroups || !countryGroups) return [];
    return countryGroups.items.filter(
      group => !userGroups.items.find(userGroup => userGroup.id === group.id)
    );
  }, [userGroups, countryGroups]);

  useEffect(() => {
    if (query?.flash) {
      const messagesMap = {
        confirmCancelRemove: 'La suppression du groupe a été annulée.',
        unauthorized: "Vous n'êtes pas autorisé à réaliser cette action.",
        error: 'Une erreur est survenue',
      };

      if (!messagesMap[query.flash as string]) {
        return;
      }
      toast({
        title: messagesMap[query.flash as string],
        status: query.flash === 'confirmCancelRemove' ? 'success' : 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }, [query?.flash]);

  return (
    <>
      <MobileBottomNav>
        <>
          <Button
            variant="sunflower"
            sx={{
              borderRadius: '0',
              height: '100%',
              fontSize: '0.875rem',
              width: selectedGroup && isMember ? 'calc(50% - 0.5px)' : '100%',
            }}
            onClick={onMobileGroupModalOpen}
          >
            Mes groupes
            <Icon as={PiUsersThin} boxSize="1rem" ml="0.625rem" />
          </Button>
          {selectedGroup && isMember && (
            <>
              <Box width="1px" background="brand.100" />
              <Button
                variant="sunflower"
                sx={{
                  borderRadius: '0',
                  height: '100%',
                  fontSize: '0.875rem',
                  width: 'calc(50% - 0.5px)',
                }}
                onClick={onMobileCreateDiscussionOpen}
              >
                Discussion
                <Icon as={PiNotePencilThin} boxSize="1rem" ml="0.625rem" />
              </Button>
            </>
          )}
        </>
      </MobileBottomNav>
      <Box
        as="section"
        mt="2rem"
        backgroundImage={`url("${BgMasqueHaut.src}")`}
        backgroundRepeat="no-repeat"
        backgroundPosition={{ base: '0 -35rem', md: '0 0' }}
        backgroundSize="512rem auto"
        backgroundColor={{ md: 'tournesol.25' }}
      >
        <Box
          backgroundImage={{ sm: `url("${VolDeMouettesCommunautes.src}")` }}
          backgroundPosition={{ sm: '0 17.5rem', md: '0 20rem' }}
          backgroundSize="100% auto"
          backgroundRepeat="no-repeat"
          pb="2rem"
        >
          <UIWidth>
            <Chapo color="tournesol.100" heading="Mes groupes">
              <Text
                fontSize={['1rem', '', '1.25rem']}
                pt={{ md: '1rem' }}
                pb="2rem"
              >
                Qu'ils soient thématiques ou liés à un projet particulier, les
                groupes vous permettent d'échanger facilement sur un sujet
                précis et de partager des documents avec ses membres.
              </Text>
            </Chapo>
            <Flex
              direction={['column', 'row']}
              justify="space-between"
              align="stretch"
              gap={['3rem', '1rem']}
              pb="2rem"
            >
              <Show breakpoint="(min-width: 834px)">
                <SimpleCard
                  width={['100%', '15rem', '19rem']}
                  accentColor="tournesol.100"
                  mt="0"
                >
                  <CardBody>
                    <GroupsHomeNavbar
                      userGroups={userGroups.items}
                      countryGroups={filtetedCountryGroups}
                      thematicsGroups={filteredThematicsGroups}
                      onClickCreateGroup={onGroupModalOpen}
                      selectedGroup={selectedGroup}
                    />
                  </CardBody>
                </SimpleCard>
              </Show>
              <Flex
                direction="column"
                gap="2rem"
                w={['100%', 'calc(100% - 15rem)', 'calc(100% - 19rem)']}
                h="fit-content"
              >
                {children}
              </Flex>
            </Flex>
          </UIWidth>
        </Box>
      </Box>

      <PublishGroupModal
        isOpen={isGroupModalOpen}
        onClose={() => {
          onGroupModalClose();
        }}
        onSubmitEnd={async (idGroup?: string) => {
          if (idGroup) {
            const group = await getGroup(idGroup);
            sendDataToGTM({
              data: {
                event: 'create_group',
                event_category: 'Groupes',
                event_action: 'Créer un groupe',
                event_label: group.name,
                country: group.country?.name ?? 'Monde',
              },
            });
          }
          onGroupModalClose();
          toast({
            title: 'Le groupe a été publié',
            status: 'success',
            duration: 9000,
            isClosable: true,
          });

          replace(route, query);
        }}
      />
      {isMobileGroupModalOpen && (
        <Modal
          isOpen={isMobileGroupModalOpen}
          onClose={onMobileGroupModalClose}
          accentColor="tournesol"
          size="full"
        >
          <ModalHeader>Groupes d'intérêts</ModalHeader>
          <ModalBody>
            <GroupsHomeNavbar
              userGroups={userGroups.items}
              countryGroups={countryGroups.items}
              thematicsGroups={thematicsGroups.items}
              onClickCreateGroup={onGroupModalOpen}
              selectedGroup={selectedGroup}
              onClickGroup={onMobileGroupModalClose}
            />
          </ModalBody>
          <ModalFooter
            borderWidth="1px 0 0 0"
            borderStyle="solid"
            borderColor="black.50"
          >
            <Button
              variant="sunflower"
              size="sm"
              w="fit-content"
              onClick={onGroupModalOpen}
            >
              Créer un groupe
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {isMobileCreateDiscussionOpen && (
        <PublishDiscussionModal
          isOpen={isMobileCreateDiscussionOpen}
          onClose={onMobileCreateDiscussionClose}
          group={selectedGroup}
          onSubmitEnd={() => {
            if (onEndPublicationCreated) {
              onEndPublicationCreated();
            }
            onMobileCreateDiscussionClose();
          }}
        />
      )}
    </>
  );
}
